// Painel de carregamentos (out/2026): tela só de acompanhamento, para todos
// verem QUEM e QUANDO estará em carregamento. Lógica pura aqui; a página só
// desenha. Horários são texto local (America/Sao_Paulo) — compara-se a chave
// "AAAA-MM-DD HH:MM" (chaveColeta), nunca Date, para não trocar o dia por UTC.
import { chaveColeta, ordenarPorColeta, situacaoHorario, type CampoCarga } from "./carga.js";

export interface LinhaPainel extends CampoCarga {
  id: number;
  empresa?: string | null;
  numero_ordem?: string | null;
  motorista?: string | null;
  cavalo?: string | null;
  cliente_entrega?: string | null;
  cidade_entrega?: string | null;
  patio_coleta?: string | null;
  tipo_veiculo?: string | null;
  peso?: number | null;
  atraso_coleta?: string | null;
}

export type EstadoPainel = "atrasada" | "agora" | "hoje" | "proxima" | "aguardando";

export const ESTADO_META: Record<EstadoPainel, { label: string; cls: string }> = {
  atrasada: { label: "Horário passou", cls: "bg-red-100 text-red-800 ring-1 ring-red-300" },
  agora: { label: "Carregando agora", cls: "bg-emerald-600 text-white" },
  hoje: { label: "Hoje", cls: "bg-sky-100 text-sky-800 ring-1 ring-sky-300" },
  proxima: { label: "Programada", cls: "bg-slate-100 text-slate-700 ring-1 ring-slate-200" },
  aguardando: { label: "Aguardando a torre", cls: "bg-amber-100 text-amber-900 ring-1 ring-amber-300" },
};

/** "Agora" em São Paulo como chave "AAAA-MM-DD HH:MM". */
export function agoraSP(d: Date = new Date()): string {
  const p = Object.fromEntries(
    new Intl.DateTimeFormat("en-CA", {
      timeZone: "America/Sao_Paulo", year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit", hourCycle: "h23",
    }).formatToParts(d).map((x) => [x.type, x.value]),
  );
  return `${p.year}-${p.month}-${p.day} ${p.hour}:${p.minute}`;
}

/** Minutos entre duas chaves "AAAA-MM-DD HH:MM" (b − a). */
function minutos(a: string, b: string): number {
  const t = (k: string) => Date.UTC(+k.slice(0, 4), +k.slice(5, 7) - 1, +k.slice(8, 10), +k.slice(11, 13), +k.slice(14, 16));
  return (t(b) - t(a)) / 60_000;
}

/** Janela em que a carga conta como "carregando agora": 1 h antes a 2 h depois do horário. */
export const JANELA_ANTES_MIN = 60;
export const JANELA_DEPOIS_MIN = 120;

/**
 * Estado da carga no painel. Sem coleta confirmada, ela ainda depende da torre
 * (o gestor só solicitou). Com coleta: dentro da janela = carregando agora;
 * passou da janela sem ser finalizada = horário passou.
 */
export function estadoCarga(c: LinhaPainel, agora: string): EstadoPainel {
  const coleta = chaveColeta(c.data_hora_coleta);
  if (!coleta || coleta.length < 16) return "aguardando";
  const delta = minutos(agora, coleta);
  if (delta < -JANELA_DEPOIS_MIN || c.atraso_coleta === "Atraso") return "atrasada";
  if (delta <= JANELA_ANTES_MIN) return "agora";
  return coleta.slice(0, 10) === agora.slice(0, 10) ? "hoje" : "proxima";
}

export interface DiaPainel { dia: string | null; linhas: (LinhaPainel & { estado: EstadoPainel })[] }

/**
 * Agrupa por dia de coleta, em sequência (dia e hora). As sem horário
 * confirmado ficam num grupo final (dia = null): ainda não têm "quando".
 */
export function montarPainel(rows: LinhaPainel[], agora: string): DiaPainel[] {
  const dias = new Map<string | null, DiaPainel>();
  for (const r of ordenarPorColeta(rows)) {
    const k = chaveColeta(r.data_hora_coleta);
    const dia = k && /^\d{4}-\d{2}-\d{2}/.test(k) ? k.slice(0, 10) : null;
    if (!dias.has(dia)) dias.set(dia, { dia, linhas: [] });
    dias.get(dia)!.linhas.push({ ...r, estado: estadoCarga(r, agora) });
  }
  return [...dias.values()];
}

export interface ResumoPainel { total: number; agora: number; hoje: number; atrasadas: number; aguardando: number }

export function resumirPainel(dias: DiaPainel[], agora: string): ResumoPainel {
  const out: ResumoPainel = { total: 0, agora: 0, hoje: 0, atrasadas: 0, aguardando: 0 };
  for (const d of dias) for (const l of d.linhas) {
    out.total++;
    if (l.estado === "agora") out.agora++;
    if (l.estado === "atrasada") out.atrasadas++;
    if (l.estado === "aguardando" || situacaoHorario(l) === "aguardando") out.aguardando++;
    if (d.dia === agora.slice(0, 10)) out.hoje++;
  }
  return out;
}

/** Dia que o painel abre: hoje se houver carga; senão o próximo; senão o último. */
export function diaInicial(dias: DiaPainel[], agora: string): string | null {
  const comDia = dias.map((d) => d.dia).filter((d): d is string => d != null);
  const hoje = agora.slice(0, 10);
  return comDia.find((d) => d >= hoje) ?? comDia[comDia.length - 1] ?? null;
}
